import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  CheckCircle2,
  Info,
  Pause,
  Play,
  Send,
  TestTube,
  X,
} from 'lucide-react';
import { PageHeader, TableSkeleton } from '@/components/shared/page';
import { Can } from '@/components/shared/can';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox, Select, Textarea } from '@/components/ui/misc';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { FormError } from '@/features/auth/auth-layout';
import { ApiRequestError } from '@/lib/api';
import { formatDate, formatDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';
import {
  useCampaign,
  useCampaignAction,
  useCampaignCheck,
  useCampaignRecipients,
  useMarketingSettings,
  useSaveCampaign,
  useScheduleCampaign,
  useSegments,
  useTestSend,
} from './api';
import {
  CAMPAIGN_STATUS_LABELS,
  CAMPAIGN_STATUS_VARIANT,
  RECIPIENT_STATUS_LABELS,
  SKIP_LABELS,
} from './labels';
import type { Campaign } from './types';

const TABS = [
  ['compose', 'Compose'],
  ['audience', 'Audience'],
  ['send', 'Check and send'],
  ['report', 'Report'],
] as const;

export function CampaignBuilderPage() {
  const { campaignId } = useParams<{ campaignId: string }>();
  const [params, setParams] = useSearchParams();
  const { data: campaign, isLoading } = useCampaign(campaignId);

  const tab = (params.get('tab') as (typeof TABS)[number][0]) ?? 'compose';

  if (isLoading) return <TableSkeleton columns={3} />;
  if (!campaign) return null;

  const sent = ['SENDING', 'SENT'].includes(campaign.status);

  return (
    <div>
      <PageHeader
        breadcrumbs={[
          { label: 'Campaigns', to: '/marketing/campaigns' },
          { label: campaign.name },
        ]}
        title={campaign.name}
        description={campaign.subject ?? 'No subject yet'}
        actions={
          <div className="flex items-center gap-2">
            <Badge variant={CAMPAIGN_STATUS_VARIANT[campaign.status]}>
              {CAMPAIGN_STATUS_LABELS[campaign.status]}
            </Badge>
            <Controls campaign={campaign} />
          </div>
        }
      />

      <div className="mb-5 flex flex-wrap gap-1 border-b">
        {TABS.map(([key, label]) => (
          <button
            key={key}
            onClick={() => setParams({ tab: key })}
            className={
              tab === key
                ? 'border-b-2 border-primary px-3 py-2 text-sm font-medium'
                : 'px-3 py-2 text-sm text-muted-foreground hover:text-foreground'
            }
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'compose' && <Compose campaign={campaign} locked={sent} />}
      {tab === 'audience' && <Audience campaign={campaign} locked={sent} />}
      {tab === 'send' && <CheckAndSend campaign={campaign} />}
      {tab === 'report' && <Report campaign={campaign} />}
    </div>
  );
}

function Controls({ campaign }: { campaign: Campaign }) {
  const action = useCampaignAction(campaign.id);

  if (campaign.status === 'SENDING') {
    return (
      <>
        <Can permission="marketing.campaign.send">
          <Button size="sm" variant="outline" onClick={() => action.mutate('pause')}>
            <Pause className="h-4 w-4" />
            Pause
          </Button>
          <Button size="sm" variant="outline" onClick={() => action.mutate('cancel')}>
            <X className="h-4 w-4" />
            Stop
          </Button>
        </Can>
      </>
    );
  }

  if (campaign.status === 'PAUSED' || campaign.status === 'SCHEDULED') {
    return (
      <Can permission="marketing.campaign.send">
        <Button size="sm" variant="outline" onClick={() => action.mutate('resume')}>
          <Play className="h-4 w-4" />
          {campaign.status === 'PAUSED' ? 'Resume' : 'Send now'}
        </Button>
        <Button size="sm" variant="outline" onClick={() => action.mutate('cancel')}>
          <X className="h-4 w-4" />
          Cancel
        </Button>
      </Can>
    );
  }

  return null;
}

function Compose({ campaign, locked }: { campaign: Campaign; locked: boolean }) {
  const save = useSaveCampaign();
  const test = useTestSend(campaign.id);
  const { data: settings } = useMarketingSettings();

  const [form, setForm] = useState({
    subject: campaign.subject ?? '',
    preheader: campaign.preheader ?? '',
    fromName: campaign.fromName ?? '',
    fromEmail: campaign.fromEmail ?? '',
    replyTo: campaign.replyTo ?? '',
    html: campaign.html ?? '',
    abEnabled: campaign.abEnabled,
    abTestPercent: campaign.abTestPercent,
    abWinnerMetric: campaign.abWinnerMetric,
  });
  const [variants, setVariants] = useState(
    campaign.variants.length > 1
      ? campaign.variants.map((variant) => ({ key: variant.key, subject: variant.subject ?? '' }))
      : [
          { key: 'A', subject: campaign.subject ?? '' },
          { key: 'B', subject: '' },
        ],
  );
  const [testTo, setTestTo] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!saved) return;
    const timer = setTimeout(() => setSaved(false), 2000);
    return () => clearTimeout(timer);
  }, [saved]);

  if (locked) {
    return (
      <Card>
        <CardContent className="space-y-4 p-6">
          <p className="text-sm text-muted-foreground">
            This campaign has gone out. What people received cannot be edited — duplicate it if you
            want to send something similar.
          </p>
          <div className="rounded-lg border bg-white p-4">
            <div className="mb-3 border-b pb-3">
              <div className="text-xs text-muted-foreground">Subject</div>
              <div className="font-medium">{campaign.subject}</div>
            </div>
            <div dangerouslySetInnerHTML={{ __html: campaign.html ?? '' }} />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid gap-5 lg:grid-cols-3">
      <div className="space-y-5 lg:col-span-2">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">The email</CardTitle>
            <CardDescription>
              Merge fields fill from the contact: <code>{'{{first_name|there}}'}</code>,{' '}
              <code>{'{{company}}'}</code>, <code>{'{{job_title}}'}</code>. A field with no
              fallback comes out blank, so give the important ones one.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <Label>Subject</Label>
              <Input
                value={form.subject}
                onChange={(event) => setForm({ ...form, subject: event.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                {form.subject.length} characters — most inboxes cut off around 60.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label>Preheader</Label>
              <Input
                value={form.preheader}
                placeholder="The line shown after the subject in the inbox"
                onChange={(event) => setForm({ ...form, preheader: event.target.value })}
              />
            </div>

            <div className="space-y-1.5">
              <Label>Body (HTML)</Label>
              <Textarea
                rows={16}
                className="font-mono text-xs"
                value={form.html}
                onChange={(event) => setForm({ ...form, html: event.target.value })}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">A/B test</CardTitle>
            <CardDescription>
              A share of the audience gets each version. The rest is held back until a winner is
              picked four hours later, which is what makes it a test rather than a coin flip.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <label className="flex cursor-pointer items-center gap-2.5">
              <Checkbox
                checked={form.abEnabled}
                onCheckedChange={(value) => setForm({ ...form, abEnabled: value === true })}
              />
              <span className="text-sm">Test two subject lines</span>
            </label>

            {form.abEnabled && (
              <>
                {variants.map((variant, index) => (
                  <div key={variant.key} className="space-y-1.5">
                    <Label>Version {variant.key}</Label>
                    <Input
                      value={variant.subject}
                      onChange={(event) => {
                        const next = [...variants];
                        next[index] = { ...variant, subject: event.target.value };
                        setVariants(next);
                      }}
                    />
                  </div>
                ))}
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label>Test share (%)</Label>
                    <Input
                      type="number"
                      min={5}
                      max={50}
                      value={form.abTestPercent}
                      onChange={(event) =>
                        setForm({ ...form, abTestPercent: Number(event.target.value) })
                      }
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Winner decided on</Label>
                    <Select
                      value={form.abWinnerMetric}
                      onChange={(event) =>
                        setForm({ ...form, abWinnerMetric: event.target.value })
                      }
                    >
                      <option value="OPEN">Open rate</option>
                      <option value="CLICK">Click rate</option>
                    </Select>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="space-y-5">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Sender</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1.5">
              <Label>From name</Label>
              <Input
                value={form.fromName}
                placeholder={settings?.fromName ?? ''}
                onChange={(event) => setForm({ ...form, fromName: event.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label>From address</Label>
              <Input
                type="email"
                value={form.fromEmail}
                placeholder={settings?.fromEmail ?? ''}
                onChange={(event) => setForm({ ...form, fromEmail: event.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Reply to</Label>
              <Input
                type="email"
                value={form.replyTo}
                onChange={(event) => setForm({ ...form, replyTo: event.target.value })}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <TestTube className="h-4 w-4 text-muted-foreground" />
              Test send
            </CardTitle>
            <CardDescription>
              Sends one copy with sample merge values, so you see what a recipient sees.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex gap-2">
            <Input
              className="h-9"
              type="email"
              placeholder="you@example.com"
              value={testTo}
              onChange={(event) => setTestTo(event.target.value)}
            />
            <Button
              size="sm"
              variant="outline"
              disabled={!testTo.includes('@') || test.isPending}
              onClick={() => test.mutate(testTo)}
            >
              Send
            </Button>
          </CardContent>
        </Card>

        <FormError message={error} />

        <Button
          className="w-full"
          disabled={save.isPending}
          onClick={async () => {
            setError(null);
            try {
              await save.mutateAsync({
                id: campaign.id,
                name: campaign.name,
                channel: campaign.channel,
                segmentId: campaign.segmentId ?? undefined,
                ...form,
                fromEmail: form.fromEmail || undefined,
                replyTo: form.replyTo || undefined,
                variants: form.abEnabled ? variants : undefined,
              });
              setSaved(true);
            } catch (caught) {
              setError(caught instanceof ApiRequestError ? caught.message : 'Could not save it.');
            }
          }}
        >
          {save.isPending ? 'Saving…' : saved ? 'Saved' : 'Save campaign'}
        </Button>
      </div>
    </div>
  );
}

function Audience({ campaign, locked }: { campaign: Campaign; locked: boolean }) {
  const save = useSaveCampaign();
  const { data: segments = [] } = useSegments();
  const { data: check } = useCampaignCheck(campaign.id);
  const [segmentId, setSegmentId] = useState(campaign.segmentId ?? '');
  const [throttle, setThrottle] = useState(campaign.throttlePerHour);
  const [error, setError] = useState<string | null>(null);

  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Who it goes to</CardTitle>
          <CardDescription>
            A dynamic segment is re-run when the campaign is scheduled, and the recipient list is
            frozen at that moment — so a segment that moves afterwards cannot change who was
            promised the email.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label>Segment</Label>
            <Select
              value={segmentId}
              disabled={locked}
              onChange={(event) => setSegmentId(event.target.value)}
            >
              <option value="">Choose an audience</option>
              {segments.map((segment) => (
                <option key={segment.id} value={segment.id}>
                  {segment.name}
                  {segment.memberCount !== null && ` (${segment.memberCount})`}
                  {segment.isDynamic ? '' : ' — static list'}
                </option>
              ))}
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Send rate</Label>
            <Input
              type="number"
              min={10}
              max={10000}
              disabled={locked}
              value={throttle}
              onChange={(event) => setThrottle(Number(event.target.value))}
            />
            <p className="text-xs text-muted-foreground">
              Messages per hour. A shared sending domain that emits four thousand in a minute has
              a reputation problem by lunchtime.
            </p>
          </div>

          <FormError message={error} />

          {!locked && (
            <Button
              disabled={save.isPending}
              onClick={async () => {
                setError(null);
                try {
                  await save.mutateAsync({
                    id: campaign.id,
                    name: campaign.name,
                    channel: campaign.channel,
                    segmentId: segmentId || undefined,
                    throttlePerHour: throttle,
                  });
                } catch (caught) {
                  setError(
                    caught instanceof ApiRequestError ? caught.message : 'Could not save it.',
                  );
                }
              }}
            >
              Save audience
            </Button>
          )}
        </CardContent>
      </Card>

      {check && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Who will actually receive it</CardTitle>
            <CardDescription>
              Everyone in the segment, minus those nothing may be sent to.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-baseline justify-between border-b pb-3">
              <span className="text-sm text-muted-foreground">In the segment</span>
              <span className="tabular text-lg font-semibold">{check.audience.inSegment}</span>
            </div>

            {Object.entries(check.audience.skipped)
              .filter(([, count]) => count > 0)
              .map(([key, count]) => (
                <div key={key} className="flex items-baseline justify-between text-sm">
                  <span className="text-muted-foreground">
                    − {SKIP_LABELS[key] ?? key}
                  </span>
                  <span className="tabular">{count}</span>
                </div>
              ))}

            <div className="flex items-baseline justify-between border-t pt-3">
              <span className="font-medium">Will receive it</span>
              <span className="tabular text-xl font-bold">{check.audience.sendable}</span>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function CheckAndSend({ campaign }: { campaign: Campaign }) {
  const { data: check, isLoading } = useCampaignCheck(campaign.id);
  const schedule = useScheduleCampaign(campaign.id);
  const [scheduling, setScheduling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ queued: number } | null>(null);

  if (isLoading || !check) return <TableSkeleton columns={2} />;

  const errors = check.issues.filter((issue) => issue.level === 'ERROR');
  const warnings = check.issues.filter((issue) => issue.level === 'WARNING');
  const alreadyOut = ['SENDING', 'SENT', 'SCHEDULED'].includes(campaign.status);

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Before it goes out</CardTitle>
          <CardDescription>
            Errors stop the send. Warnings do not — they are judgement calls that belong to
            whoever wrote the email.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {check.issues.length === 0 ? (
            <div className="flex items-center gap-2 text-sm text-emerald-600">
              <CheckCircle2 className="h-4 w-4" />
              Everything checks out.
            </div>
          ) : (
            <>
              {errors.map((issue, index) => (
                <div
                  key={`e${index}`}
                  className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2.5 text-sm"
                >
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                  <span>{issue.message}</span>
                </div>
              ))}
              {warnings.map((issue, index) => (
                <div
                  key={`w${index}`}
                  className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm text-amber-900"
                >
                  <Info className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{issue.message}</span>
                </div>
              ))}
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Audience</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm">
            <span className="tabular text-2xl font-bold">{check.audience.sendable}</span>
            <span className="ms-2 text-muted-foreground">
              of {check.audience.inSegment} in the segment will receive it
            </span>
          </p>
        </CardContent>
      </Card>

      {result ? (
        <div className="rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
          <CheckCircle2 className="me-2 inline h-4 w-4" />
          {result.queued} messages queued. They go out at the rate you set.
        </div>
      ) : alreadyOut ? (
        <div className="rounded-lg border bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
          This campaign is already {CAMPAIGN_STATUS_LABELS[campaign.status].toLowerCase()}.
        </div>
      ) : (
        <>
          <FormError message={error} />
          <Can permission="marketing.campaign.send">
            <div className="flex flex-wrap gap-3">
              <Button
                size="lg"
                disabled={!check.canSend || schedule.isPending}
                onClick={async () => {
                  setError(null);
                  try {
                    const outcome = await schedule.mutateAsync({ sendNow: true });
                    setResult(outcome as { queued: number });
                  } catch (caught) {
                    setError(
                      caught instanceof ApiRequestError ? caught.message : 'Could not send it.',
                    );
                  }
                }}
              >
                <Send className="h-4 w-4" />
                Send now
              </Button>
              <Button
                size="lg"
                variant="outline"
                disabled={!check.canSend}
                onClick={() => setScheduling(true)}
              >
                Schedule
              </Button>
            </div>
          </Can>
        </>
      )}

      {scheduling && (
        <ScheduleDialog
          campaignId={campaign.id}
          onClose={() => setScheduling(false)}
          onScheduled={(queued) => {
            setResult({ queued });
            setScheduling(false);
          }}
        />
      )}
    </div>
  );
}

function ScheduleDialog({
  campaignId,
  onClose,
  onScheduled,
}: {
  campaignId: string;
  onClose: () => void;
  onScheduled: (queued: number) => void;
}) {
  const schedule = useScheduleCampaign(campaignId);
  const [when, setWhen] = useState('');
  const [error, setError] = useState<string | null>(null);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Schedule the send</DialogTitle>
          <DialogDescription>
            The recipient list is frozen now, not at send time.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Send at</Label>
            <Input
              type="datetime-local"
              value={when}
              onChange={(event) => setWhen(event.target.value)}
            />
          </div>
          <FormError message={error} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!when || schedule.isPending}
            onClick={async () => {
              setError(null);
              try {
                const outcome = (await schedule.mutateAsync({
                  scheduledAt: new Date(when).toISOString(),
                })) as { queued: number };
                onScheduled(outcome.queued);
              } catch (caught) {
                setError(
                  caught instanceof ApiRequestError ? caught.message : 'Could not schedule it.',
                );
              }
            }}
          >
            Schedule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Report({ campaign }: { campaign: Campaign }) {
  const [status, setStatus] = useState('');
  const { data: recipients = [] } = useCampaignRecipients(campaign.id, status || undefined);

  if (campaign.stats.total === 0) {
    return (
      <Card>
        <CardContent className="py-14 text-center text-sm text-muted-foreground">
          Nothing to report yet — this campaign has not been sent.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-5">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Metric label="Sent" value={campaign.stats.sent} />
        <Metric label="Opened" value={campaign.stats.opened} rate={campaign.stats.openRate} />
        <Metric label="Clicked" value={campaign.stats.clicked} rate={campaign.stats.clickRate} />
        <Metric
          label="Unsubscribed"
          value={campaign.stats.unsubscribed}
          rate={campaign.stats.unsubscribeRate}
          bad
        />
      </div>

      {campaign.variantStats && campaign.variantStats.length > 1 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">A/B result</CardTitle>
            {campaign.abWinnerKey && (
              <CardDescription>
                Version {campaign.abWinnerKey} won and went to the rest of the audience
                {campaign.abDecidedAt && `, decided ${formatDateTime(campaign.abDecidedAt)}`}.
              </CardDescription>
            )}
          </CardHeader>
          <CardContent className="space-y-3">
            {campaign.variantStats.map((variant) => (
              <div
                key={variant.key}
                className={cn(
                  'rounded-lg border p-3',
                  campaign.abWinnerKey === variant.key && 'border-primary/40 bg-primary/5',
                )}
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0">
                    <span className="font-medium">Version {variant.key}</span>
                    <span className="ms-2 truncate text-sm text-muted-foreground">
                      {variant.subject}
                    </span>
                  </div>
                  <div className="tabular flex gap-4 text-sm">
                    <span>{variant.sent} sent</span>
                    <span className="font-medium">
                      {variant.openRate === null ? '—' : `${variant.openRate}% opened`}
                    </span>
                    <span>
                      {variant.clickRate === null ? '—' : `${variant.clickRate}% clicked`}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {campaign.topLinks && campaign.topLinks.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">What people clicked</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {campaign.topLinks.map((link) => (
              <div key={link.url} className="flex items-center justify-between gap-3 text-sm">
                <span className="min-w-0 truncate text-muted-foreground">{link.url}</span>
                <span className="tabular shrink-0 font-medium">{link.clicks}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="flex-row items-start justify-between pb-3">
          <CardTitle className="text-base">Recipients</CardTitle>
          <Select
            className="h-8 w-auto text-xs"
            value={status}
            onChange={(event) => setStatus(event.target.value)}
          >
            <option value="">Everyone</option>
            {Object.entries(RECIPIENT_STATUS_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </CardHeader>
        <CardContent className="overflow-x-auto p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Person</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-end">Opens</TableHead>
                <TableHead className="text-end">Clicks</TableHead>
                <TableHead>Sent</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recipients.slice(0, 200).map((recipient) => (
                <TableRow key={recipient.id}>
                  <TableCell>
                    <div className="text-sm font-medium">{recipient.name ?? recipient.email}</div>
                    <div className="text-xs text-muted-foreground">{recipient.email}</div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">
                      {RECIPIENT_STATUS_LABELS[recipient.status] ?? recipient.status}
                    </span>
                    {recipient.skipReason && (
                      <div className="text-xs text-muted-foreground">{recipient.skipReason}</div>
                    )}
                    {recipient.failureReason && (
                      <div className="text-xs text-destructive">{recipient.failureReason}</div>
                    )}
                  </TableCell>
                  <TableCell className="tabular text-end text-sm">
                    {recipient.openCount || '—'}
                  </TableCell>
                  <TableCell className="tabular text-end text-sm">
                    {recipient.clickCount || '—'}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {recipient.sentAt ? formatDate(recipient.sentAt) : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function Metric({
  label,
  value,
  rate,
  bad,
}: {
  label: string;
  value: number;
  rate?: number | null;
  bad?: boolean;
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="tabular mt-1 text-2xl font-bold tracking-tight">{value}</p>
        {rate !== null && rate !== undefined && (
          <p className={cn('mt-0.5 text-xs', bad ? 'text-amber-600' : 'text-muted-foreground')}>
            {rate}% of those sent
          </p>
        )}
      </CardContent>
    </Card>
  );
}
